import { datadogRum } from '@datadog/browser-rum'
import { generateUUID } from './generateUUID'
import { getRiseClient } from './riseClient'
import { eventLoaders, commonLoader } from './eventRegistry'

// Legacy trackEvent IDs that don't match RISE event naming conventions.
// New events should use the RISE event name directly as the trackEvent ID
// (e.g., "catalog.item_updated" not "catalog.item.updated").
const LEGACY_ID_ALIASES: Record<string, string> = {
  'navV2.navigation.action': 'navigation.action',
  'auth.refresh.attempted': 'auth_refresh.attempted',
  'auth.refresh.success': 'auth_refresh.success',
}

// Module caches — each event module is loaded independently on first use.
const eventModuleCache = new Map<string, Promise<Record<string, any> | null>>()
let commonModulePromise: Promise<Record<string, any> | null> | null = null

function loadEventModule(trackEventId: string): Promise<Record<string, any> | null> {
  const cached = eventModuleCache.get(trackEventId)
  if (cached) return cached

  const riseEventName = LEGACY_ID_ALIASES[trackEventId] ?? trackEventId
  const loader = eventLoaders[riseEventName]
  if (!loader) return Promise.resolve(null)

  const promise = loader().catch(() => null) as Promise<Record<string, any> | null>
  eventModuleCache.set(trackEventId, promise)
  return promise
}

function loadCommonModule(): Promise<Record<string, any> | null> {
  if (!commonModulePromise) {
    commonModulePromise = commonLoader().catch(() => null) as Promise<Record<string, any> | null>
  }
  return commonModulePromise
}

function getSessionId(): string {
  try {
    const rumContext = datadogRum.getInternalContext()
    if (rumContext?.session_id) return rumContext.session_id
  } catch {
    // DD RUM may not be initialized
  }
  return generateUUID()
}

function getPageViewId(): string {
  try {
    const rumContext = datadogRum.getInternalContext()
    if (rumContext?.view?.id) return rumContext.view.id
  } catch {
    // DD RUM may not be initialized
  }
  return generateUUID()
}

export function trackRiseEvent({
  id,
  data,
}: {
  id: string
  description: string
  data?: Record<string, any>
}): void {
  try {
    const client = getRiseClient()
    if (!client) return

    const eventData: Record<string, any> = {
      // Default url to current page — events that need it (e.g. app.page_view)
      // get a value automatically; events that don't will ignore it.
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      ...(data ?? {}),
    }

    Promise.all([loadEventModule(id), loadCommonModule()])
      .then(([eventModule, commonModule]) => {
        if (!eventModule || !commonModule) return

        // Each codegen event file exports exactly one class
        const EventClass = Object.values(eventModule).find(
          (v): v is new (p: any) => any => typeof v === 'function'
        )
        if (!EventClass) return

        const event = new EventClass(eventData)
        const common = new commonModule.CommonExternalProperties({
          partnerId: eventData.partnerId != null ? String(eventData.partnerId) : undefined,
          retailerId: eventData.retailerId != null ? String(eventData.retailerId) : undefined,
          storeConfigId:
            eventData.storeConfigId != null ? String(eventData.storeConfigId) : undefined,
          canonicalId: eventData.canonicalId != null ? String(eventData.canonicalId) : undefined,
          isInternal: eventData.isInternal,
          sessionId: getSessionId(),
          pageViewId: getPageViewId(),
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        })
        client.track(event, common)
      })
      .catch(() => {
        // RISE failures must never break existing tracking
      })
  } catch {
    // RISE failures must never break existing tracking
  }
}
